import type {
  AgentRun,
  CalendarDay,
  CalendarSlot,
  ContentPiece,
  DeckVersion,
  IdeaCandidate,
  PlanningWeek,
  Theme,
  ThemeCandidate,
} from "./types";

export const themeCandidates: ThemeCandidate[] = [
  {
    id: "theme-week-1",
    title: "Fast Fresh Stops",
    rationale: "Make the nearby market feel like the nearest answer for summer errands.",
    assignedWeeks: [1],
    source: "guided_ai",
    color: "green",
  },
  {
    id: "theme-week-2",
    title: "Neighborhood Lunch Break",
    rationale: "Build proximity around quick cold meals, snacks, and practical weekday choices.",
    assignedWeeks: [2],
    source: "ai_generated",
    color: "blue",
  },
  {
    id: "theme-candidate-3",
    title: "Beach Bag Top-Up",
    rationale: "Connect Express shopping to last-minute beach, picnic, and family outing needs.",
    assignedWeeks: [4],
    source: "account_manager",
    color: "amber",
  },
  {
    id: "theme-candidate-4",
    title: "Throne Day Readiness",
    rationale: "Plan respectful convenience content around the July 30 national holiday.",
    assignedWeeks: [5],
    source: "ai_generated",
    color: "rose",
  },
  {
    id: "theme-candidate-5",
    title: "After-Work Express",
    rationale: "Position quick evening top-ups for commuters, parents, and nearby residents.",
    assignedWeeks: [],
    source: "ai_generated",
    color: "blue",
  },
  {
    id: "theme-candidate-6",
    title: "Weekend Hosting Helpers",
    rationale: "Frame Express as the easy stop for snacks, drinks, desserts, and last-minute guests.",
    assignedWeeks: [],
    source: "ai_generated",
    color: "green",
  },
];

export const themes: Theme[] = themeCandidates.flatMap((candidate) =>
  candidate.assignedWeeks.map((week) => ({
    id: candidate.id,
    week,
    title: candidate.title,
    rationale: candidate.rationale,
  })),
);

export const contentPieces: ContentPiece[] = [
  {
    id: "piece-001",
    title: "The Forgotten Item Run",
    contentType: "reel",
    week: 1,
    dateLabel: "Tue, Jul 7",
    themeId: "theme-week-1",
    stage: "asset_in_review",
    owner: "Mina",
    designer: "Yassine",
    briefSummary: "A three-cut errand story showing how the nearby market saves a summer evening.",
    assetStatus: "in_review",
    clientStatus: "not_sent",
  },
  {
    id: "piece-002",
    title: "Cold Break Vote",
    contentType: "story",
    week: 1,
    dateLabel: "Thu, Jul 2",
    themeId: "theme-week-1",
    stage: "brief_ready",
    owner: "Mina",
    briefSummary: "Interactive poll between cold drink and fruit snack choices for a midday break.",
    assetStatus: "placeholder",
    clientStatus: "not_sent",
  },
  {
    id: "piece-003",
    title: "Lunch Without Detour",
    contentType: "post",
    week: 2,
    dateLabel: "Mon, Jul 13",
    themeId: "theme-week-2",
    stage: "brief_draft",
    owner: "Amit",
    briefSummary: "Carousel concept for building a fast lunch basket from nearby essentials.",
    assetStatus: "placeholder",
    clientStatus: "not_sent",
  },
  {
    id: "piece-004",
    title: "Holiday Top-Up Without Rush",
    contentType: "reel",
    week: 5,
    dateLabel: "Thu, Jul 30",
    themeId: "theme-candidate-4",
    stage: "client_change_requested",
    owner: "Mina",
    designer: "Yassine",
    briefSummary: "Client wants a respectful convenience angle for the Feast of the Throne holiday.",
    assetStatus: "approved",
    clientStatus: "changes_requested",
  },
];

type WeekSlotSeed = Omit<CalendarSlot, "id" | "guidance" | "locked">;
type WeekDaySeed = Omit<CalendarDay, "id" | "slots"> & {
  slots?: WeekSlotSeed[];
};

function makeWeekDays(week: number, days: WeekDaySeed[], locked = false): CalendarDay[] {
  return days.map((day) => {
    const dayKey = day.dayLabel.toLowerCase();
    return {
      id: `day-${week}-${dayKey}`,
      dayLabel: day.dayLabel,
      dateNumber: day.dateNumber,
      monthLabel: day.monthLabel,
      inMonth: day.inMonth,
      holidayLabel: day.holidayLabel,
      slots: day.inMonth ? makeDaySlots(week, dayKey, day.slots ?? [], locked) : [],
    };
  });
}

function makeDaySlots(week: number, dayKey: string, slots: WeekSlotSeed[], locked = false): CalendarSlot[] {
  return slots.map((slot, index) => {
    const guidance = locked ? "Choose theme first" : `Drop ${contentTypeLabel(slot.contentType)} here`;
    const base = {
      id: `slot-${week}-${dayKey}${index ? `-${index + 1}` : ""}`,
      guidance,
      ...slot,
    };
    return locked ? { ...base, locked: true } : base;
  });
}

function contentTypeLabel(contentType: CalendarSlot["contentType"]): string {
  return contentType.charAt(0).toUpperCase() + contentType.slice(1);
}

export const planningWeeks: PlanningWeek[] = [
  {
    id: "week-1",
    week: 1,
    themeId: "theme-week-1",
    status: "theme_set",
    note: "Theme is set. Ideas can be generated, edited, and placed.",
    dateRange: "Mon, Jun 29 to Sun, Jul 5",
    days: makeWeekDays(1, [
      { dayLabel: "Mon", dateNumber: 29, monthLabel: "Jun", inMonth: false },
      { dayLabel: "Tue", dateNumber: 30, monthLabel: "Jun", inMonth: false },
      { dayLabel: "Wed", dateNumber: 1, inMonth: true, slots: [{ contentType: "post" }] },
      { dayLabel: "Thu", dateNumber: 2, inMonth: true, slots: [{ contentType: "story", pieceId: "piece-002" }] },
      { dayLabel: "Fri", dateNumber: 3, inMonth: true, slots: [{ contentType: "reel", pieceId: "piece-001" }] },
      { dayLabel: "Sat", dateNumber: 4, inMonth: true, slots: [{ contentType: "story" }] },
      { dayLabel: "Sun", dateNumber: 5, inMonth: true, slots: [{ contentType: "reel" }] },
    ]),
  },
  {
    id: "week-2",
    week: 2,
    dateRange: "Mon, Jul 6 to Sun, Jul 12",
    themeId: "theme-week-2",
    status: "theme_set",
    note: "Theme is set. Ideas can be generated, edited, and placed.",
    days: makeWeekDays(2, [
      { dayLabel: "Mon", dateNumber: 6, inMonth: true, slots: [{ contentType: "post" }] },
      { dayLabel: "Tue", dateNumber: 7, inMonth: true, slots: [{ contentType: "story" }] },
      { dayLabel: "Wed", dateNumber: 8, inMonth: true, slots: [{ contentType: "post" }] },
      { dayLabel: "Thu", dateNumber: 9, inMonth: true, slots: [{ contentType: "story" }] },
      { dayLabel: "Fri", dateNumber: 10, inMonth: true, slots: [{ contentType: "reel" }] },
      { dayLabel: "Sat", dateNumber: 11, inMonth: true },
      { dayLabel: "Sun", dateNumber: 12, inMonth: true, slots: [{ contentType: "reel" }] },
    ]),
  },
  {
    id: "week-3",
    week: 3,
    dateRange: "Mon, Jul 13 to Sun, Jul 19",
    status: "needs_theme",
    note: "Choose a theme before generating or placing ideas for this week.",
    days: makeWeekDays(
      3,
      [
        { dayLabel: "Mon", dateNumber: 13, inMonth: true, slots: [{ contentType: "post", pieceId: "piece-003" }] },
        { dayLabel: "Tue", dateNumber: 14, inMonth: true, slots: [{ contentType: "reel" }] },
        { dayLabel: "Wed", dateNumber: 15, inMonth: true, slots: [{ contentType: "story" }] },
        { dayLabel: "Thu", dateNumber: 16, inMonth: true, slots: [{ contentType: "post" }] },
        { dayLabel: "Fri", dateNumber: 17, inMonth: true, slots: [{ contentType: "post" }] },
        { dayLabel: "Sat", dateNumber: 18, inMonth: true, slots: [{ contentType: "reel" }] },
        { dayLabel: "Sun", dateNumber: 19, inMonth: true, slots: [{ contentType: "story" }] },
      ],
      true,
    ),
  },
  {
    id: "week-4",
    week: 4,
    dateRange: "Mon, Jul 20 to Sun, Jul 26",
    themeId: "theme-candidate-3",
    status: "theme_set",
    note: "Theme is set. Ideas can be generated, edited, and placed.",
    days: makeWeekDays(4, [
      { dayLabel: "Mon", dateNumber: 20, inMonth: true, slots: [{ contentType: "post" }] },
      { dayLabel: "Tue", dateNumber: 21, inMonth: true, slots: [{ contentType: "story" }] },
      { dayLabel: "Wed", dateNumber: 22, inMonth: true, slots: [{ contentType: "story" }] },
      { dayLabel: "Thu", dateNumber: 23, inMonth: true, slots: [{ contentType: "reel" }] },
      { dayLabel: "Fri", dateNumber: 24, inMonth: true, slots: [{ contentType: "story" }] },
      { dayLabel: "Sat", dateNumber: 25, inMonth: true, slots: [{ contentType: "post" }] },
      { dayLabel: "Sun", dateNumber: 26, inMonth: true, slots: [{ contentType: "reel" }] },
    ]),
  },
  {
    id: "week-5",
    week: 5,
    dateRange: "Mon, Jul 27 to Sun, Aug 2",
    themeId: "theme-candidate-4",
    status: "theme_set",
    note: "Theme is set. Holiday context stays visible while ideas are placed.",
    days: makeWeekDays(5, [
      { dayLabel: "Mon", dateNumber: 27, inMonth: true, slots: [{ contentType: "post" }] },
      { dayLabel: "Tue", dateNumber: 28, inMonth: true, slots: [{ contentType: "story" }] },
      { dayLabel: "Wed", dateNumber: 29, inMonth: true, slots: [{ contentType: "story" }] },
      {
        dayLabel: "Thu",
        dateNumber: 30,
        inMonth: true,
        holidayLabel: "Feast of the Throne",
        slots: [{ contentType: "reel", pieceId: "piece-004" }],
      },
      { dayLabel: "Fri", dateNumber: 31, inMonth: true, slots: [{ contentType: "post" }] },
      { dayLabel: "Sat", dateNumber: 1, monthLabel: "Aug", inMonth: false },
      { dayLabel: "Sun", dateNumber: 2, monthLabel: "Aug", inMonth: false },
    ]),
  },
];

export const ideaCandidates: IdeaCandidate[] = [
  {
    id: "idea-001",
    title: "Forgotten Item Run",
    contentType: "reel",
    week: 1,
    themeId: "theme-week-1",
    status: "placed",
    source: "ai_generated",
    summary: "A quick errand story built around the one item missing before dinner.",
    fitScore: 94,
  },
  {
    id: "idea-002",
    title: "Cold Break Vote",
    contentType: "story",
    week: 1,
    themeId: "theme-week-1",
    status: "placed",
    source: "ai_generated",
    summary: "A poll that lets followers choose the smarter midday cooling stop.",
    fitScore: 89,
  },
  {
    id: "idea-003",
    title: "Seven Minute Basket",
    contentType: "post",
    week: 1,
    themeId: "theme-week-1",
    status: "selected",
    source: "manual",
    summary: "A practical basket list for the quickest useful Express trip.",
    fitScore: 86,
  },
  {
    id: "idea-004",
    title: "Lunch Without Detour",
    contentType: "post",
    week: 2,
    themeId: "theme-week-2",
    status: "placed",
    source: "ai_generated",
    summary: "A near-office lunch basket that avoids a long midday detour.",
    fitScore: 82,
  },
  {
    id: "idea-005",
    title: "Beach Bag Before You Go",
    contentType: "reel",
    week: 4,
    themeId: "theme-candidate-3",
    status: "available",
    source: "manual",
    summary: "A drag-ready concept for the Week 4 beach and outing theme.",
    fitScore: 78,
  },
  {
    id: "idea-006",
    title: "Holiday Top-Up Without Rush",
    contentType: "reel",
    week: 5,
    themeId: "theme-candidate-4",
    status: "placed",
    source: "client_requested",
    summary: "A respectful convenience idea for the July 30 Feast of the Throne context.",
    fitScore: 80,
  },
];

export const agentRuns: AgentRun[] = [
  {
    id: "run-001",
    label: "High-quality idea selection",
    taskType: "select_finalists",
    status: "complete",
    progress: 100,
    costTier: "high",
  },
  {
    id: "run-002",
    label: "Draft briefs for placed ideas",
    taskType: "draft_briefs",
    status: "running",
    progress: 62,
    costTier: "low",
  },
  {
    id: "run-003",
    label: "Generate placeholder visuals",
    taskType: "generate_placeholders",
    status: "queued",
    progress: 0,
    costTier: "low",
  },
];

export const deckVersions: DeckVersion[] = [
  {
    id: "deck-v1",
    label: "July proposal v1",
    status: "changes_requested",
    sentAt: "2026-06-24",
    approvedPieces: 2,
    totalPieces: 4,
  },
];
